// --------------------------------------------------------------
// Created On: 2025-09-30
// Author: Zachary Thomas
//
// Last Modified: 2025-11-05
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React from "react";
import Modal from "../Modal/Modal";
import ModalHeader from "../ModalHeader/ModalHeader";
import ModalBody from "../ModalBody/ModalBody";
import ModalFooter from "../ModalFooter/ModalFooter";
import Error from "src/components/Error/Error";
import styles from "./MfaConfirmModal.module.scss";

// Modal for confirming a critical action that needs an MFA code to proceed.
export default function MfaConfirmModal(props: Props): Component {
  return (
    <div data-test="mfa-confirm-modal">
      <Modal
        show={props.showModal}
        onHide={() => props.onCancel()}
        backdropClassName={`${styles.modal} ${styles.backdrop}`}
        style={{ zIndex: "var(--super-modal-z-index)" }}
        animation
        centered
      >
        <ModalHeader>
          <h5>{props.title}</h5>
        </ModalHeader>
        <ModalBody>
          <p>{props.content}</p>
          <br />
          <p>Enter your MFA code in the box below to confirm.</p>

          {/* The pattern for this input is always numbers, but it is a string. For example '000349' is valid. */}
          <input
            data-test="mfa-code-input"
            className="login-input form-control my-2"
            pattern="\d*"
            aria-describedby="MFA Code"
            placeholder="Enter MFA code..."
            value={props.code}
            disabled={props.disabled}
            onChange={(e) => props.onChangeCode(e.target.value)}
          />

          {props.errorMessage !== undefined && props.errorMessage.length > 0 && (
            <div className="mt-3">
              <Error message={props.errorMessage} />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <button
            data-test="mfa-confirm-button"
            type="button"
            className="btn btn-primary"
            data-dismiss="modal"
            onClick={() => props.onConfirm()}
            disabled={props.disabled || props.code.length === 0}
          >
            {props.confirmText}
          </button>

          <button
            data-test="cancel-mfa-modal-button"
            type="button"
            className="btn btn-secondary"
            onClick={() => props.onCancel()}
          >
            Cancel
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

interface Props {
  showModal: boolean;
  title: string;
  content: string;
  code: string;
  confirmText: string;
  errorMessage?: string;
  disabled?: boolean;
  onChangeCode: (code: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}
